/**
 * In-memory map packs (owner B1).
 *
 * The loader streams every pack into RAM (src/features/loader/preload.ts), so tiles are served
 * from memory: no HTTP Range requests, no per-tile network, and the kiosk works offline.
 *
 *  pmtiles://<name>              TileJSON (pmtiles Protocol, metadata on) — the vector basemap.
 *  pmtiles://<name>/{z}/{x}/{y}  the same archive as a plain tile template (B2's ≤ z11 underlay).
 *  ldem://<name>/{z}/{x}/{y}     raster-dem tiles. A tile the DEM pack does not hold (z10–12 lie
 *                                only inside the metro box) is synthesised from its nearest ancestor:
 *                                decode terrarium → crop → upsample the elevations → re-encode
 *                                terrarium → PNG. So hillshade reaches Lonavala, Navi Mumbai and
 *                                Chakan at every zoom instead of stopping at 73.75°E.
 *
 * Source JSON for B2 (also exported below as functions):
 *   basemap   { type: "vector", url: BASEMAP_URL }
 *   underlay  { type: "vector", tiles: [BASEMAP_TILE_URL], minzoom: 0, maxzoom: 11, bounds: REGION_BBOX }
 *   dem       { type: "raster-dem", tiles: [DEM_TILE_URL], tileSize: 512, encoding: "terrarium",
 *               minzoom: 0, maxzoom: 12, bounds: REGION_BBOX }   ← add it after "dem:ready"
 *
 * Call ensurePmtilesProtocol() (registerMemoryArchive() does it) before `new maplibregl.Map()`.
 */
import * as maplibregl from "maplibre-gl";
import type { AddProtocolAction, GetResourceResponse, AddProtocolResponseData, RequestParameters } from "maplibre-gl";
import type { RasterDEMSourceSpecification, VectorSourceSpecification } from "@maplibre/maplibre-gl-style-spec";
import { PMTiles, Protocol, TileType, type RangeResponse, type Source } from "pmtiles";
import { REGION_BBOX } from "@/contracts/taxonomy";
import { Lru, ancestorOf, decodeTerrarium, encodeTerrarium, parseTileUrl, upsampleQuadrant, type Rgba, type Upsample } from "@/features/loader/dem";

/** TileJSON-style URL for the vector basemap source (style: { type: "vector", url: BASEMAP_URL }). */
export const BASEMAP_URL = "pmtiles://basemap";
/** The same archive as a tile template (no TileJSON round trip), e.g. for the ≤ z11 underlay source. */
export const BASEMAP_TILE_URL = "pmtiles://basemap/{z}/{x}/{y}";
/** Custom protocol that serves DEM tiles and fills the z10–12 gap from ancestors. */
export const DEM_PROTOCOL = "ldem";
/** Tile template for the raster-dem source; B1's handler synthesises missing z10–12 tiles from ancestors. */
export const DEM_TILE_URL = "ldem://dem/{z}/{x}/{y}";
export const DEM_TILE_SIZE = 512;
export const DEM_MAXZOOM = 12;

/* ─── Source JSON (B2 builds its style from these) ───────────────────────── */

export function basemapSource(): VectorSourceSpecification {
  return { type: "vector", url: BASEMAP_URL };
}
/** The region pack drawn at ≤ z11 under the detailed basemap, so z12+ outside metro/corridors never shows blank paper. */
export function underlaySource(): VectorSourceSpecification {
  return { type: "vector", tiles: [BASEMAP_TILE_URL], minzoom: 0, maxzoom: 11, bounds: [...REGION_BBOX] };
}
/** Add only after "dem:ready" (store.demReady): the ldem handler rejects until the DEM is in memory. */
export function demSource(): RasterDEMSourceSpecification {
  return {
    type: "raster-dem",
    tiles: [DEM_TILE_URL],
    tileSize: DEM_TILE_SIZE,
    encoding: "terrarium",
    minzoom: 0,
    maxzoom: DEM_MAXZOOM,
    bounds: [...REGION_BBOX],
  };
}

/* ─── The in-memory Source ───────────────────────────────────────────────── */

/**
 * A pmtiles Source over one ArrayBuffer. getBytes ALWAYS returns a copy: MapLibre transfers tile
 * buffers to its workers, and a view on the master buffer would detach the whole archive.
 */
export class MemorySource implements Source {
  constructor(
    private readonly name: string,
    private readonly buf: ArrayBuffer,
  ) {}
  getKey(): string {
    return this.name;
  }
  async getBytes(offset: number, length: number, signal?: AbortSignal): Promise<RangeResponse> {
    signal?.throwIfAborted();
    const start = Math.max(0, Math.min(offset, this.buf.byteLength));
    const end = Math.max(start, Math.min(offset + length, this.buf.byteLength));
    return { data: this.buf.slice(start, end) };
  }
  get byteLength(): number {
    return this.buf.byteLength;
  }
}

/* ─── Registry + protocols ───────────────────────────────────────────────── */

const archives = new Map<string, PMTiles>();
let protocol: Protocol | null = null;

/** "pmtiles://basemap" or "pmtiles://basemap/12/2889/1832(.mvt)" → "basemap". */
function pmtilesName(url: string): string | null {
  if (!url.startsWith("pmtiles://")) return null;
  return url.slice(10).replace(/\/\d+\/\d+\/\d+[^/]*$/, "") || null;
}

/** Idempotently installs the pmtiles:// and ldem:// protocols on maplibre-gl. */
export function ensurePmtilesProtocol(): void {
  if (protocol) return;
  const p = new Protocol({ metadata: true });
  protocol = p;
  const pm: AddProtocolAction = (params, ac) => {
    const name = pmtilesName(params.url);
    // Guard: an unknown name would make pmtiles create a *fetching* source (Range requests to
    // /basemap). Everything here lives in memory, so fail loudly instead.
    if (!name || !archives.has(name)) return Promise.reject(new Error(`pmtiles archive "${name ?? params.url}" is not in memory`));
    return p.tile(params as Parameters<Protocol["tilev4"]>[0], ac) as Promise<GetResourceResponse<AddProtocolResponseData>>;
  };
  maplibregl.addProtocol("pmtiles", pm);
  maplibregl.addProtocol(DEM_PROTOCOL, demTile);
}

/** Register an archive held in memory so the style can use `pmtiles://<name>` (and `ldem://<name>` for the DEM). */
export function registerMemoryArchive(name: string, buf: ArrayBuffer): void {
  ensurePmtilesProtocol();
  const archive = new PMTiles(new MemorySource(name, buf));
  archives.set(name, archive);
  protocol!.add(archive);
  clearDemCaches();
}

export function hasArchive(name: string): boolean {
  return archives.has(name);
}

/** The registered archive (debug / harness). */
export function getArchive(name: string): PMTiles | undefined {
  return archives.get(name);
}

/* ─── DEM: native tiles + ancestor synthesis ─────────────────────────────── */

export interface DemCodec {
  /** Decode an encoded image into straight (non-premultiplied) RGBA. */
  decode(bytes: ArrayBuffer, mime: string): Promise<{ width: number; height: number; data: Rgba }>;
  /** Encode RGBA as an image MapLibre can decode (PNG). */
  encode(rgba: Uint8ClampedArray<ArrayBuffer>, width: number, height: number): Promise<ArrayBuffer>;
}

type Canvas2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

let scratch: { canvas: OffscreenCanvas | HTMLCanvasElement; ctx: Canvas2D } | null = null;

/** Browser codec: createImageBitmap + (Offscreen)Canvas. Protocol handlers run on the main thread. */
export const browserCodec: DemCodec = {
  async decode(bytes, mime) {
    const bmp = await createImageBitmap(new Blob([bytes], { type: mime }), { premultiplyAlpha: "none", colorSpaceConversion: "none" });
    try {
      const { width, height } = bmp;
      if (!scratch || scratch.canvas.width !== width || scratch.canvas.height !== height) {
        const canvas = makeCanvas(width, height);
        const ctx = canvas.getContext("2d", { willReadFrequently: true }) as Canvas2D | null;
        if (!ctx) throw new Error("2D canvas unavailable for DEM decoding");
        scratch = { canvas, ctx };
      }
      // Drawing and reading back happen in one synchronous run, so the scratch canvas is safe to share.
      scratch.ctx.clearRect(0, 0, width, height);
      scratch.ctx.drawImage(bmp, 0, 0);
      const img = scratch.ctx.getImageData(0, 0, width, height);
      return { width, height, data: img.data };
    } finally {
      bmp.close();
    }
  },
  async encode(rgba, width, height) {
    const canvas = makeCanvas(width, height);
    const ctx = canvas.getContext("2d") as Canvas2D | null;
    if (!ctx) throw new Error("2D canvas unavailable for DEM encoding");
    ctx.putImageData(new ImageData(rgba, width, height), 0, 0);
    const blob =
      "convertToBlob" in canvas
        ? await canvas.convertToBlob({ type: "image/png" })
        : await new Promise<Blob>((res, rej) => (canvas as HTMLCanvasElement).toBlob((b) => (b ? res(b) : rej(new Error("PNG encoding failed"))), "image/png"));
    return blob.arrayBuffer();
  },
};

let codec: DemCodec = browserCodec;
/** Upsampling used for synthesised tiles. Bilinear per the brief; see dem.ts for "bicubic". */
let upsample: Upsample = "bilinear";
/** Deepest ancestor gap to bridge (z12 from z9 is 3; beyond 5 the crop is < 16 px and meaningless). */
const MAX_DZ = 5;

// Room for the pre-synthesised flight tiles (prewarmDem) plus what the visitor explores.
const synthCache = new Lru<string, ArrayBuffer>(128, 64 * 2 ** 20, (b) => b.byteLength);
const synthJobs = new Map<string, Promise<ArrayBuffer | null>>();
const ancestorCache = new Lru<string, Float32Array>(6); // decoded ancestors, 1 MiB each at 512²
const ancestorJobs = new Map<string, Promise<Float32Array | null>>();

/** Counters for the harness / QA overlay. */
export const demStats = { native: 0, synthesized: 0, cacheHits: 0, empty: 0, lastSynthMs: 0 };

export function setDemCodec(c: DemCodec | null): void {
  codec = c ?? browserCodec;
  clearDemCaches();
}
export function setDemUpsample(m: Upsample): void {
  if (m === upsample) return;
  upsample = m;
  clearDemCaches();
}
export function getDemUpsample(): Upsample {
  return upsample;
}

/**
 * Synthesised tiles depend on the archive, the codec and the upsampling: any change drops them. The
 * generation is bumped with them; the worker keys its decoded ancestors by it.
 */
let demGen = 0;
function clearDemCaches(): void {
  demGen++;
  synthCache.clear();
  synthJobs.clear();
  ancestorCache.clear();
  ancestorJobs.clear();
}

const MIME: Partial<Record<TileType, string>> = {
  [TileType.Png]: "image/png",
  [TileType.Jpeg]: "image/jpeg",
  [TileType.Webp]: "image/webp",
  [TileType.Avif]: "image/avif",
};

const empty = (): GetResourceResponse<AddProtocolResponseData> => ({ data: new ArrayBuffer(0) }); // MapLibre: loaded, no DEM

function abortIf(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
}

/** The ldem:// handler. Exported for tests and the harness; MapLibre calls it through addProtocol. */
export async function demTile(params: RequestParameters, ac: AbortController): Promise<GetResourceResponse<AddProtocolResponseData>> {
  const t = parseTileUrl(params.url, DEM_PROTOCOL);
  if (!t) throw new Error(`Invalid DEM tile URL: ${params.url}`);
  const archive = archives.get(t.name);
  if (!archive) throw new Error(`DEM archive "${t.name}" is not in memory yet (add the source after "dem:ready")`);
  const header = await archive.getHeader();
  if (t.z < header.minZoom || t.z > header.maxZoom) {
    demStats.empty++;
    return empty();
  }
  const hit = await archive.getZxy(t.z, t.x, t.y, ac.signal);
  if (hit) {
    demStats.native++;
    return { data: hit.data }; // a fresh copy (MemorySource.getBytes slices)
  }
  const key = `${t.name}/${t.z}/${t.x}/${t.y}`;
  const cached = synthCache.get(key);
  if (cached) {
    demStats.cacheHits++;
    return { data: cached.slice(0) };
  }
  let job = synthJobs.get(key);
  if (!job) {
    // Shared by every requester of this tile, so it is never aborted by one of them.
    job = synthesize(archive, t.name, t.z, t.x, t.y, MIME[header.tileType as TileType] ?? "image/webp").finally(() => synthJobs.delete(key));
    synthJobs.set(key, job);
  }
  const png = await job;
  abortIf(ac.signal);
  if (!png) {
    demStats.empty++;
    return empty();
  }
  return { data: png.slice(0) };
}

/* ─── The DEM worker (L2-04) ─────────────────────────────────────────────── */

/**
 * With the browser codec, gap tiles are synthesised in a module worker (demWorker.ts: decode → crop →
 * upsample → terrarium → PNG, the same dem.ts functions), so a flight over the gap never stalls the main
 * thread. A worker that cannot start or fails once hands the job back to the main-thread path for good.
 */
let worker: Worker | null = null;
let workerBroken = typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined";
let workerSeq = 0;
const workerJobs = new Map<number, { resolve: (b: ArrayBuffer) => void; reject: (e: Error) => void }>();

function demWorker(): Worker | null {
  if (workerBroken || codec !== browserCodec) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL("./demWorker.ts", import.meta.url), { type: "module", name: "ld-dem" });
    worker.onmessage = (e: MessageEvent<import("./demWorker").DemReply>) => {
      const r = e.data;
      const job = workerJobs.get(r.id);
      if (!job) return;
      workerJobs.delete(r.id);
      if ("png" in r) job.resolve(r.png);
      else job.reject(new Error(r.error));
    };
    worker.onerror = (e) => {
      console.warn("[dem] worker failed; synthesising on the main thread", e.message);
      retireWorker();
    };
  } catch (err) {
    console.warn("[dem] worker unavailable; synthesising on the main thread", err);
    retireWorker();
  }
  return worker;
}
function retireWorker(): void {
  workerBroken = true;
  worker?.terminate();
  worker = null;
  for (const j of workerJobs.values()) j.reject(new Error("DEM worker stopped"));
  workerJobs.clear();
}

function synthInWorker(w: Worker, key: string, bytes: ArrayBuffer, mime: string, a: { dz: number; qx: number; qy: number }): Promise<ArrayBuffer> {
  const id = ++workerSeq;
  return new Promise<ArrayBuffer>((resolve, reject) => {
    workerJobs.set(id, { resolve, reject });
    w.postMessage({ id, key, bytes, mime, dz: a.dz, qx: a.qx, qy: a.qy, method: upsample }, [bytes]);
  });
}

async function synthesize(archive: PMTiles, name: string, z: number, x: number, y: number, mime: string): Promise<ArrayBuffer | null> {
  const t0 = performance.now();
  const w = demWorker();
  if (w) {
    try {
      for (let az = z - 1; az >= Math.max(0, z - MAX_DZ); az--) {
        const a = ancestorOf(z, x, y, az);
        const tile = await archive.getZxy(a.z, a.x, a.y);
        if (!tile) continue;
        const png = await synthInWorker(w, `${demGen}:${name}/${a.z}/${a.x}/${a.y}`, tile.data, mime, a);
        synthCache.set(`${name}/${z}/${x}/${y}`, png);
        demStats.synthesized++;
        demStats.lastSynthMs = Math.round(performance.now() - t0);
        return png;
      }
      return null;
    } catch (err) {
      if (!workerBroken) console.warn("[dem] worker synthesis failed; using the main thread", err);
      retireWorker();
    }
  }
  for (let az = z - 1; az >= Math.max(0, z - MAX_DZ); az--) {
    const a = ancestorOf(z, x, y, az);
    const elev = await ancestorElevations(archive, name, a.z, a.x, a.y, mime);
    if (!elev) continue;
    const n = Math.round(Math.sqrt(elev.length));
    const up = upsampleQuadrant(elev, n, a.dz, a.qx, a.qy, upsample);
    const png = await codec.encode(encodeTerrarium(up), n, n);
    synthCache.set(`${name}/${z}/${x}/${y}`, png);
    demStats.synthesized++;
    demStats.lastSynthMs = Math.round(performance.now() - t0);
    return png;
  }
  return null;
}

function ancestorElevations(archive: PMTiles, name: string, z: number, x: number, y: number, mime: string): Promise<Float32Array | null> {
  const key = `${name}/${z}/${x}/${y}`;
  const cached = ancestorCache.get(key);
  if (cached) return Promise.resolve(cached);
  let job = ancestorJobs.get(key);
  if (!job) {
    job = (async () => {
      const tile = await archive.getZxy(z, x, y);
      if (!tile) return null;
      const img = await codec.decode(tile.data, mime);
      if (img.width !== img.height) throw new Error(`DEM tile ${key} is ${img.width}×${img.height}, expected a square`);
      const elev = decodeTerrarium(img.data);
      ancestorCache.set(key, elev);
      return elev;
    })().finally(() => ancestorJobs.delete(key));
    ancestorJobs.set(key, job);
  }
  return job;
}

/**
 * Elevation (m) at a point, read the way MapLibre reads it: through the ldem handler at zoom z,
 * then bilinear inside the tile. For QA and the harness; not used on the render path.
 */
export async function sampleDemElevation(lng: number, lat: number, z: number, name = "dem"): Promise<{ elevation: number; source: "native" | "synthesized" | "empty" } | null> {
  if (!archives.has(name)) return null;
  const n = 2 ** z;
  const fx = ((lng + 180) / 360) * n;
  const s = Math.sin((lat * Math.PI) / 180);
  const fy = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n;
  const x = Math.floor(fx);
  const y = Math.floor(fy);
  const before = demStats.native;
  const res = await demTile({ url: `${DEM_PROTOCOL}://${name}/${z}/${x}/${y}` } as RequestParameters, new AbortController());
  const buf = res.data as ArrayBuffer;
  if (!buf.byteLength) return { elevation: NaN, source: "empty" };
  const header = await archives.get(name)!.getHeader();
  const native = demStats.native > before;
  const img = await codec.decode(buf, native ? (MIME[header.tileType as TileType] ?? "image/webp") : "image/png");
  const elev = decodeTerrarium(img.data);
  const size = img.width;
  const px = (fx - x) * size - 0.5;
  const py = (fy - y) * size - 0.5;
  const x0 = Math.max(0, Math.min(size - 2, Math.floor(px)));
  const y0 = Math.max(0, Math.min(size - 2, Math.floor(py)));
  const tx = px - x0;
  const ty = py - y0;
  const at = (i: number, j: number) => elev[j * size + i];
  const top = at(x0, y0) + (at(x0 + 1, y0) - at(x0, y0)) * tx;
  const bot = at(x0, y0 + 1) + (at(x0 + 1, y0 + 1) - at(x0, y0 + 1)) * tx;
  return { elevation: top + (bot - top) * ty, source: native ? "native" : "synthesized" };
}

/* ─── Pre-synthesis (L2-04, L2-09) ───────────────────────────────────────── */

/** Tile ids (z, x, y) of zoom `z` covering a [w, s, e, n] box. */
export function tilesInBox(b: readonly [number, number, number, number], z: number): Array<[number, number, number]> {
  const n = 2 ** z;
  const tx = (lng: number) => Math.floor(((lng + 180) / 360) * n);
  const ty = (lat: number) => {
    const s = Math.sin((Math.max(-85.05, Math.min(85.05, lat)) * Math.PI) / 180);
    return Math.floor((0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n);
  };
  const out: Array<[number, number, number]> = [];
  for (let y = ty(b[3]); y <= ty(b[1]); y++) for (let x = tx(b[0]); x <= tx(b[2]); x++) out.push([z, x, y]);
  return out;
}

/**
 * Pre-synthesises the DEM gap tiles that the intro's fly and the first long flights pass through (by
 * default z10 over REGION_BBOX), one at a time, so the hillshade never waits on synthesis mid-flight.
 * Tiles the pack holds, or that are already cached, cost nothing. Resolves with the number synthesised.
 */
export async function prewarmDem(o: { tiles?: Array<[number, number, number]>; name?: string; signal?: AbortSignal } = {}): Promise<number> {
  const name = o.name ?? "dem";
  if (!archives.has(name)) return 0;
  const tiles = o.tiles ?? tilesInBox(REGION_BBOX, 10);
  const before = demStats.synthesized;
  for (const [z, x, y] of tiles) {
    if (o.signal?.aborted) break;
    try {
      await demTile({ url: `${DEM_PROTOCOL}://${name}/${z}/${x}/${y}` } as RequestParameters, new AbortController());
    } catch {
      /* a tile that cannot be made now is made on demand */
    }
    // yield between tiles: the first frames and the visitor's input come first
    await new Promise<void>((r) => setTimeout(r, 16));
  }
  return demStats.synthesized - before;
}
